import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { api, errorMessage } from '@/lib/client'
import { DICE_MAX_TARGET, DICE_MIN_TARGET, diceMultiplier, dicePayout } from '@/lib/games'
import { formatMultiplier, formatShort } from '@/lib/money'
import { play } from '@/lib/sounds'
import { cn } from '@/lib/utils'
import { useSession } from '@/hooks/use-session'
import { Button } from '@/components/generals/button'
import { Arena, BetInput, GameLayout, Panel, StatBox, useStake } from '@/components/games/game-ui'
import { usePlay } from '@/components/games/use-play'

type RollResponse = {
    target: number
    roll: number
    won: boolean
    payout: number
    balance: number
}

const SCRAMBLE_MS = 650

const DiceGame: FC = (): ReactNode => {
    const { maintenance, setBalance } = useSession()
    const { canBet, toast } = usePlay()
    const stake = useStake()
    const [target, setTarget] = useState(50)
    const [rolling, setRolling] = useState(false)
    const [display, setDisplay] = useState<number | null>(null)
    const [result, setResult] = useState<RollResponse | null>(null)
    const scramble = useRef(0)
    const settle = useRef(0)

    useEffect(() => () => {
        window.clearInterval(scramble.current)
        window.clearTimeout(settle.current)
    }, [])

    const roll = async () => {
        if (rolling || !canBet(stake.amount)) return

        setRolling(true)
        setResult(null)
        play('roll')

        // Shuffle the digits while the server rolls.
        scramble.current = window.setInterval(() => setDisplay(Math.floor(Math.random() * 10_000) / 100), 55)

        try {
            const started = Date.now()
            const data = await api<RollResponse>('/api/games/dice', { body: { amount: stake.amount, target } })

            settle.current = window.setTimeout(() => {
                window.clearInterval(scramble.current)
                setDisplay(data.roll)
                setResult(data)
                setRolling(false)
                setBalance(data.balance)
                play(data.won ? 'win' : 'lose')
            }, Math.max(0, SCRAMBLE_MS - (Date.now() - started)))
        }
        catch (caught) {
            window.clearInterval(scramble.current)
            setDisplay(null)
            setRolling(false)
            toast(errorMessage(caught), 'error')
        }
    }

    const multiplier = diceMultiplier(target)
    const payout = stake.amount ? dicePayout(stake.amount, target) : 0
    const fill = ((target - DICE_MIN_TARGET) / (DICE_MAX_TARGET - DICE_MIN_TARGET)) * 100
    const markerAt = result ? result.roll : display ?? target

    let status: ReactNode = 'Slide to set your odds'

    if (rolling) status = 'Rolling…'
    else if (result?.won) status = <span className='text-success'>Rolled {result.roll.toFixed(2)}. You won {formatShort(result.payout)}</span>
    else if (result) status = <span className='text-danger'>Rolled {result.roll.toFixed(2)}, needed under {result.target}. Not this time</span>

    const panel = (
        <Panel>
            <BetInput value={stake.text} onChange={stake.setText} disabled={rolling} />

            <div>
                <label htmlFor='dice-target' className='mb-2.5 block text-sm font-semibold text-foreground/90'>
                    Roll under <span className='text-foreground'>{target}</span>
                </label>

                <input
                    id='dice-target'
                    type='range'
                    min={DICE_MIN_TARGET}
                    max={DICE_MAX_TARGET}
                    step={1}
                    value={target}
                    disabled={rolling}
                    onChange={(event) => setTarget(Number(event.target.value))}
                    className='df-range'
                    style={{ '--fill': `${fill}%` } as CSSProperties}
                    aria-valuetext={`Roll under ${target}, ${target}% win chance`}
                />
            </div>

            <div className='grid grid-cols-2 gap-2'>
                <StatBox label='Win chance' value={`${target}%`} />
                <StatBox label='Multiplier' value={formatMultiplier(multiplier)} />
            </div>

            <p className='-mt-1 text-xs text-muted-foreground'>
                Payout on win: <span className='font-semibold text-foreground'>{stake.amount ? formatShort(payout) : '–'}</span>
            </p>

            <Button size='lg' className='w-full' onClick={roll} loading={rolling} disabled={maintenance}>
                {maintenance ? 'Paused for maintenance' : rolling ? 'Rolling' : 'Roll dice'}
            </Button>
        </Panel>
    )

    const arena = (
        <Arena className='flex flex-col items-center justify-center px-5 py-10 sm:px-10'>
            <p
                className={cn(
                    'font-heading text-[4.5rem] leading-none font-bold tabular-nums sm:text-[6.5rem]',
                    result ? (result.won ? 'text-success drop-shadow-[0_0_24px_rgba(61,220,132,0.35)]' : 'text-danger drop-shadow-[0_0_24px_rgba(255,90,74,0.3)]') : 'text-foreground'
                )}
                aria-hidden='true'
            >
                {display === null ? '00.00' : display.toFixed(2).padStart(5, '0')}
            </p>

            <div className='mt-8 w-full max-w-xl'>
                <div className='relative h-8'>
                    <div
                        className='absolute -translate-x-1/2 transition-[left] duration-200'
                        style={{ left: `${Math.min(100, Math.max(0, markerAt))}%` }}
                        aria-hidden='true'
                    >
                        <span className={cn(
                            'block rounded-md px-1.5 py-0.5 text-xs font-bold',
                            result ? (result.won ? 'bg-success text-[#062615]' : 'bg-danger text-white') : 'bg-foreground text-background'
                        )}
                        >
                            {display === null ? '–' : display.toFixed(2)}
                        </span>
                        <span className={cn('mx-auto block size-0 border-x-[5px] border-t-[6px] border-x-transparent', result ? (result.won ? 'border-t-success' : 'border-t-danger') : 'border-t-foreground')} />
                    </div>
                </div>

                <div className='flex h-3 overflow-hidden rounded-full' aria-hidden='true'>
                    <div className='bg-success transition-[width] duration-200' style={{ width: `${target}%` }} />
                    <div className='flex-1 bg-danger' />
                </div>

                <div className='mt-3 flex justify-between text-xs text-muted-foreground' aria-hidden='true'>
                    {[0, 25, 50, 75, 100].map((tick) => (
                        <span key={tick}>{tick}</span>
                    ))}
                </div>
            </div>

            <p className='mt-8 text-center text-lg font-bold sm:text-xl' aria-live='polite'>
                {status}
            </p>
        </Arena>
    )

    return <GameLayout panel={panel} arena={arena} />
}

export default DiceGame
